import * as React from 'react';
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Visibility, VisibilityOff, Login, Email, Lock } from '@mui/icons-material';
import { LoaderOverlay } from '../utils/components';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '../forms/schemas';
import { useNotificationManager } from '../../hooks/useNotificationManager';
import logoImage from '../../logo192.png';

const theme = createTheme();

// Styled components para elementos modernos
const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: '25px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 32px',
  fontSize: '1.1rem',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
  },
}));

const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '15px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
    '&.Mui-focused': {
      backgroundColor: 'rgba(255,255,255,1)',
      transform: 'translateY(-1px)',
    },
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

export default function SignIn({ usePageTitle }) {
  usePageTitle('PixTick | Signin');
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmittingLogin, setIsSubmittingLogin] = React.useState(false);
  const { isLoggedIn, loading, login } = useAuth();
  const { showSuccess } = useNotificationManager();

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  // Redirecionar se já estiver logado
  React.useEffect(() => {
    if (isLoggedIn && !loading) {
      navigate('/relatorio-pagamento');
    }
  }, [isLoggedIn, loading, navigate]);

  const onSubmit = async (data) => {
    // Evitar múltiplas submissões
    if (isSubmittingLogin) {
      return;
    }

    setIsSubmittingLogin(true);

    try {
      const result = await login(data.email, data.password);

      if (result.success) {
        showSuccess("Login realizado com sucesso!", {
          description: "Redirecionando para o painel...",
          duration: 2000,
        });

        setTimeout(() => {
          navigate('/relatorio-pagamento');
        }, 1000);
      }
      // Não mostrar toast de erro aqui pois o AuthContext já cuida disso

    } catch (error) {
      console.log('Erro no login:', error);
      // Não mostrar toast de erro aqui pois o AuthContext já cuida disso
    } finally {
      setIsSubmittingLogin(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 2 },
        // Ajustar para mobile - mesmo padrão do perfil
        pt: { xs: 'calc(64px + max(env(safe-area-inset-top, 0px), 10px))', sm: 0 },
        pb: { xs: 2, sm: 0 }
      }}>
        <LoaderOverlay loading={loading} />
        <CssBaseline />

        <Card
          elevation={10}
          sx={{
            maxWidth: 450,
            width: '100%',
            borderRadius: 4,
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255,255,255,0.95)',
            // Ajustar para mobile - mesmo padrão do perfil
            maxWidth: { xs: '100%', sm: 450 },
            margin: { xs: 0, sm: 2 }
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                textAlign: 'center',
                mb: 3,
                fontWeight: 600,
                color: '#333',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1
              }}
            >
              <Login color="primary" />
              Entrar
            </Typography>

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ModernTextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email"
                    autoComplete="email"
                    autoFocus
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ModernTextField
                    {...field}
                    margin="normal"
                    required
                    fullWidth
                    label="Senha"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />

              <ModernButton
                type="submit"
                fullWidth
                variant="contained"
                disabled={isSubmitting || isSubmittingLogin}
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#1976D2',
                  '&:hover': {
                    backgroundColor: '#1565C0',
                  }
                }}
              >
                {(isSubmitting || isSubmittingLogin) ? 'Entrando...' : 'Entrar'}
              </ModernButton>

              <Divider sx={{ my: 2 }} />

              <Grid container justifyContent="center">
                <Grid item>
                  <Link
                    href="/ForgotPassword"
                    variant="body2"
                    sx={{
                      color: '#1976D2',
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Esqueceu a senha?
                  </Link>
                </Grid>
              </Grid>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
